import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

/**
 * PIN Verification Modal for Transaction Authorization
 * 
 * Usage:
 * <PinVerificationModal
 *   isOpen={showPinModal}
 *   onClose={() => setShowPinModal(false)}
 *   onVerified={() => { proceedWithTransaction(); }}
 *   transactionType="AgentDeposit"
 *   amount={10000}
 * />
 */
export default function PinVerificationModal({ 
  isOpen, 
  onClose, 
  onVerified, 
  transactionType = "",
  amount = null,
  title = "Enter Transaction PIN"
}) {
  const [pin, setPin] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showBackupCode, setShowBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    if (isOpen) {
      setPin(["", "", "", ""]);
      setError("");
      setShowBackupCode(false);
      setBackupCode("");
      // Focus first input
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
  }, [isOpen]);

  const handlePinChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError("");

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 3 && newPin.every(d => d !== "")) {
      handleVerify(newPin.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Handle paste
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, "").slice(0, 4).split("");
        const newPin = ["", "", "", ""];
        digits.forEach((d, i) => { if (i < 4) newPin[i] = d; });
        setPin(newPin);
        if (digits.length === 4) {
          handleVerify(newPin.join(""));
        }
      });
    }
  };

  const handleVerify = async (pinValue) => {
    if (!pinValue || pinValue.length < 4) {
      setError("Please enter your 4-digit PIN");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.post("/TwoFactor/verify", {
        pin: pinValue,
        transactionType: transactionType,
        amount: amount
      });

      if (res.data.verified) {
        onVerified();
        onClose();
      } else {
        setError("Invalid PIN");
        setPin(["", "", "", ""]);
        inputRefs[0].current?.focus();
      }
    } catch (err) {
      const message = err.response?.data?.message || "Verification failed";
      setError(message);
      setPin(["", "", "", ""]);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodeVerify = async () => {
    if (!backupCode) {
      setError("Please enter a backup code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.post("/TwoFactor/backup-codes/verify", {
        code: backupCode
      });

      if (res.data.verified) {
        onVerified();
        onClose();
      } else {
        setError("Invalid backup code");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>🔐 {title}</h2>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {!showBackupCode ? (
          <>
            <p style={styles.subtitle}>
              Enter your 4-digit transaction PIN to authorize
              {transactionType && <span style={styles.transactionInfo}> {transactionType}</span>}
              {amount && <span style={styles.amountInfo}> of ₦{amount.toLocaleString()}</span>}
            </p>

            <div style={styles.pinContainer}>
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={inputRefs[index]}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  style={{
                    ...styles.pinInput,
                    borderColor: error ? "#dc3545" : digit ? "#28a745" : "#ddd"
                  }}
                  disabled={loading}
                />
              ))}
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.actions}>
              <button
                onClick={() => handleVerify(pin.join(""))}
                disabled={loading || pin.some(d => !d)}
                style={{
                  ...styles.btnPrimary,
                  opacity: loading || pin.some(d => !d) ? 0.6 : 1
                }}
              >
                {loading ? "Verifying..." : "Verify PIN"}
              </button>
            </div>

            <div style={styles.footer}>
              <button 
                onClick={() => setShowBackupCode(true)} 
                style={styles.linkBtn}
              >
                Use backup code instead
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={styles.subtitle}>
              Enter one of your backup codes to unlock your account
            </p>

            <input
              type="text"
              placeholder="Enter backup code"
              value={backupCode}
              onChange={(e) => { setBackupCode(e.target.value.toUpperCase()); setError(""); }}
              style={styles.backupInput}
              disabled={loading}
              maxLength={8}
            />

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.actions}>
              <button
                onClick={handleBackupCodeVerify}
                disabled={loading || !backupCode}
                style={{
                  ...styles.btnPrimary,
                  opacity: loading || !backupCode ? 0.6 : 1
                }}
              >
                {loading ? "Verifying..." : "Verify Backup Code"}
              </button>
            </div>

            <div style={styles.footer}>
              <button 
                onClick={() => { setShowBackupCode(false); setError(""); }}
                style={styles.linkBtn}
              >
                ← Back to PIN entry
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000
  },
  modal: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "30px",
    width: "100%",
    maxWidth: "380px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.2)"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px"
  },
  title: {
    margin: 0,
    fontSize: "20px"
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#666",
    padding: "5px"
  },
  subtitle: {
    textAlign: "center",
    color: "#666",
    marginBottom: "25px",
    fontSize: "14px"
  },
  transactionInfo: {
    color: "#007bff",
    fontWeight: "500"
  },
  amountInfo: {
    color: "#28a745",
    fontWeight: "600"
  },
  pinContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "20px"
  },
  pinInput: {
    width: "55px",
    height: "60px",
    textAlign: "center",
    fontSize: "24px",
    fontWeight: "bold",
    border: "2px solid #ddd",
    borderRadius: "12px",
    outline: "none",
    transition: "border-color 0.2s"
  },
  backupInput: {
    width: "100%",
    padding: "15px",
    fontSize: "18px",
    textAlign: "center",
    fontFamily: "monospace",
    letterSpacing: "3px",
    border: "2px solid #ddd",
    borderRadius: "8px",
    marginBottom: "20px"
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "10px 15px",
    borderRadius: "8px",
    textAlign: "center",
    marginBottom: "15px",
    fontSize: "14px"
  },
  actions: {
    display: "flex",
    justifyContent: "center"
  },
  btnPrimary: {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "14px 40px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    width: "100%"
  },
  footer: {
    marginTop: "20px",
    textAlign: "center"
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#007bff",
    cursor: "pointer",
    fontSize: "13px",
    textDecoration: "underline"
  }
};

